import { useEffect } from 'react'
import Combo from 'app/components/ui/combo/combo'
import type { RoleJson } from 'app/types/entities'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

interface RoleSelectorProps {
  workspaceRoles: RoleJson[]
  role: number
  roleId: string
  disabled?: boolean
  onChange: (role: number | undefined, roleId: string | undefined) => void
  setIsDirtyForm: (isDirty: boolean) => void
}

export function RoleSelector({ workspaceRoles, role, roleId, disabled, onChange, setIsDirtyForm }: RoleSelectorProps) {
  useEffect(() => {
    debug(`roleSelector`, `state updated`, { role, roleId })
  }, [role, roleId])

  useEffect(() => {
    debug(
      `roleSelector`,
      `workspaceRoles received`,
      workspaceRoles.map((r) => ({ id: r.id, name: r.name })),
    )
  }, [workspaceRoles])

  const options = workspaceRoles
    .filter((role) => role.id != null)
    .map(
      (role) =>
        ({
          id: role.id!,
          name: role.name,
          value: role.id!,
        }) as OptionType,
    )

  const value = (() => {
    if (!roleId) return undefined
    const foundRole = workspaceRoles.find((r) => r.id === roleId)
    if (!foundRole || !foundRole.name) return undefined
    return { id: foundRole.id!, name: foundRole.name, value: foundRole.id! }
  })()

  const handleChange = (option: OptionType) => {
    debug(`roleSelector`, `role selected`, { id: option.id, name: option.name, value: option.value })
    onChange(undefined, option.value)
    setIsDirtyForm(true)
  }

  return (
    <div className="col-span-6 mb-4" data-testid="role-selector">
      <label className="required text-ui-sm">Role</label>
      <Combo
        className="disabled:bg-surface-subtle"
        inputClassName="text-body-sm text-strong"
        options={options}
        value={value}
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  )
}
